import React, { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { X, CheckSquare, Square, Upload, FileText, Check } from "lucide-react";

const AVModal = ({ onClose, onSuccess }) => {
  const { api } = useAuth();
  const [certify, setCertify] = useState(false);
  const [form, setForm] = useState({ parent_name: "", parent_email: "", parent_phone: "", player_name: "", player_dob: "", jersey_number: "" });
  const [docs, setDocs] = useState({ birth_cert: null, id_front: null, id_back: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const birthCertRef = useRef(null);
  const idFrontRef = useRef(null);
  const idBackRef = useRef(null);

  const handleFile = (key, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setDocs(prev => ({ ...prev, [key]: { name: file.name, size: file.size, data: reader.result } }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!certify) { setError("You must certify as parent/guardian"); return; }
    setSubmitting(true); setError("");
    try {
      const docNames = {};
      if (docs.birth_cert) docNames.birth_certificate = docs.birth_cert.name;
      if (docs.id_front) docNames.state_id_front = docs.id_front.name;
      if (docs.id_back) docNames.state_id_back = docs.id_back.name;

      await api().post("/av/submit", {
        ...form, certify_guardian: true, documents: docNames,
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed");
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px', background: '#1a2230', border: '1px solid #2a3a4a',
    borderRadius: 10, color: '#fff', fontSize: 15, outline: 'none', fontFamily: "'DM Sans', sans-serif",
  };

  const DocUpload = ({ label, docKey, fileRef, icon }) => {
    const doc = docs[docKey];
    return (
      <div
        data-testid={`doc-upload-${docKey}`}
        onClick={() => fileRef.current?.click()}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: doc ? 'rgba(76,175,80,0.08)' : '#1a2230',
          border: `1px solid ${doc ? 'rgba(76,175,80,0.3)' : '#2a3a4a'}`,
          borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s',
        }}
      >
        {doc ? <Check size={18} style={{ color: '#4caf50' }} /> : icon}
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: doc ? '#4caf50' : '#fff' }}>
            {doc ? doc.name : label}
          </p>
          <p style={{ fontSize: 11, color: doc ? '#4caf50' : '#888' }}>
            {doc ? `${(doc.size / 1024).toFixed(1)} KB - Uploaded` : 'Tap to upload'}
          </p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf"
          style={{ display: 'none' }}
          onChange={e => handleFile(docKey, e.target.files[0])}
        />
      </div>
    );
  };

  return (
    <div data-testid="av-modal" style={{
      position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: '#0d1117', border: '1px solid #1e2832', borderRadius: 16,
        maxWidth: 500, width: '100%', maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e2832', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 data-testid="av-modal-title" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 700, color: '#fff' }}>Age Verification</h2>
            <p style={{ color: '#888', fontSize: 13 }}>Complete player verification for your league</p>
          </div>
          <button data-testid="av-close-btn" onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}><X size={22} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
          {error && <div style={{ background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 8, padding: '10px 14px', color: '#f44336', fontSize: 13, marginBottom: 16 }}>{error}</div>}

          {/* Certify checkbox */}
          <button type="button" data-testid="av-certify-btn" onClick={() => setCertify(!certify)} style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 16px',
            background: certify ? 'rgba(0,229,255,0.08)' : '#1a2230', border: `1px solid ${certify ? 'var(--cyan)' : '#2a3a4a'}`,
            borderRadius: 10, cursor: 'pointer', marginBottom: 20, textAlign: 'left',
          }}>
            {certify ? <CheckSquare size={22} style={{ color: 'var(--cyan)', flexShrink: 0 }} /> : <Square size={22} style={{ color: '#555', flexShrink: 0 }} />}
            <span style={{ color: '#fff', fontSize: 14 }}>I certify that I am the parent/legal guardian of the player</span>
          </button>

          {/* Parent/Guardian Information */}
          <h3 style={{ color: 'var(--cyan)', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Parent/Guardian Information</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            <input data-testid="av-parent-name" style={inputStyle} placeholder="Parent/Guardian Full Name *" value={form.parent_name} onChange={e => setForm({...form, parent_name: e.target.value})} required />
            <input data-testid="av-parent-email" style={inputStyle} type="email" placeholder="Parent Email *" value={form.parent_email} onChange={e => setForm({...form, parent_email: e.target.value})} required />
            <input data-testid="av-parent-phone" style={inputStyle} type="tel" placeholder="Parent Phone Number *" value={form.parent_phone} onChange={e => setForm({...form, parent_phone: e.target.value})} required />
          </div>

          {/* Player Information */}
          <h3 style={{ color: 'var(--cyan)', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Player Information</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            <input data-testid="av-player-name" style={inputStyle} placeholder="Player Full Name *" value={form.player_name} onChange={e => setForm({...form, player_name: e.target.value})} required />
            <input data-testid="av-player-dob" style={inputStyle} type="date" placeholder="Player Date of Birth *" value={form.player_dob} onChange={e => setForm({...form, player_dob: e.target.value})} required />
            <input data-testid="av-jersey-number" style={inputStyle} placeholder="Jersey Number (optional)" value={form.jersey_number} onChange={e => setForm({...form, jersey_number: e.target.value})} />
          </div>

          {/* Documents */}
          <h3 style={{ color: 'var(--cyan)', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Documents</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            <DocUpload label="Birth Certificate" docKey="birth_cert" fileRef={birthCertRef} icon={<Upload size={18} style={{ color: '#888' }} />} />
            <DocUpload label="State ID (Front)" docKey="id_front" fileRef={idFrontRef} icon={<FileText size={18} style={{ color: '#888' }} />} />
            <DocUpload label="State ID (Back)" docKey="id_back" fileRef={idBackRef} icon={<FileText size={18} style={{ color: '#888' }} />} />
          </div>

          {/* Submit */}
          <button type="submit" data-testid="av-submit-btn" disabled={submitting} style={{
            width: '100%', padding: '16px', background: '#00e676', border: 'none',
            borderRadius: 12, color: '#000', fontSize: 16, fontWeight: 700,
            cursor: 'pointer', opacity: submitting ? 0.7 : 1, fontFamily: "'Outfit', sans-serif",
          }}>{submitting ? 'Submitting...' : 'Submit Age Verification'}</button>
        </form>
      </div>
    </div>
  );
};

export default AVModal;
